import { AppleLogo, ArrowSquareOut, Desktop, LinuxLogo } from "@phosphor-icons/react";
import { type ReactNode, type RefObject, useEffect, useRef, useState } from "react";

interface PlatformButtonProps {
	icon: ReactNode;
	platform: string;
	format: string;
	href: string;
}

interface DownloadProps {
	description: string;
}

const RELEASES_PAGE = "https://github.com/Dhaxor/clapio-release/releases";

// Fallback links baked into the prerendered HTML (kept current at deploy time).
// When the download section nears the viewport, the newest release is resolved
// from the public GitHub API. Until then, these serve every visitor without adding
// a third-party request to the page's critical rendering path.
const FALLBACK_VERSION = "1.4.0";
const FALLBACK_LINKS: DownloadLinks = {
	version: FALLBACK_VERSION,
	macArm: `${RELEASES_PAGE}/download/v${FALLBACK_VERSION}/Clapio-Mac-arm64-${FALLBACK_VERSION}-Installer.dmg`,
	macIntel: `${RELEASES_PAGE}/download/v${FALLBACK_VERSION}/Clapio-Mac-x64-${FALLBACK_VERSION}-Installer.dmg`,
	windows: `${RELEASES_PAGE}/download/v${FALLBACK_VERSION}/Clapio-Setup-${FALLBACK_VERSION}.exe`,
	linux: `${RELEASES_PAGE}/download/v${FALLBACK_VERSION}/Clapio-Linux-${FALLBACK_VERSION}.AppImage`,
};

interface DownloadLinks {
	version: string;
	macArm: string;
	macIntel: string;
	windows: string;
	linux: string;
}

interface GitHubAsset {
	name: string;
	browser_download_url: string;
}

function resolveLinksFromRelease(release: {
	tag_name?: string;
	assets?: GitHubAsset[];
}): DownloadLinks | null {
	const assets = release.assets ?? [];
	const find = (test: (name: string) => boolean) =>
		assets.find((a) => !a.name.endsWith(".blockmap") && test(a.name))?.browser_download_url;

	const macArm = find((n) => n.includes("Mac-arm64") && n.endsWith(".dmg"));
	const macIntel = find((n) => n.includes("Mac-x64") && n.endsWith(".dmg"));
	const windows = find((n) => n.endsWith(".exe"));
	const linux = find((n) => n.endsWith(".AppImage"));

	if (!macArm || !macIntel || !windows || !linux) return null;
	return {
		version: (release.tag_name ?? "").replace(/^v/, "") || FALLBACK_VERSION,
		macArm,
		macIntel,
		windows,
		linux,
	};
}

function useLatestReleaseLinks(sectionRef: RefObject<HTMLElement | null>): DownloadLinks {
	const [links, setLinks] = useState<DownloadLinks>(FALLBACK_LINKS);

	useEffect(() => {
		let cancelled = false;
		let observer: IntersectionObserver | null = null;

		const loadLatestRelease = () => {
			fetch("https://api.github.com/repos/Dhaxor/clapio-release/releases/latest", {
				headers: { Accept: "application/vnd.github+json" },
			})
				.then((res) => (res.ok ? res.json() : null))
				.then((release) => {
					if (cancelled || !release) return;
					const resolved = resolveLinksFromRelease(release);
					if (resolved) setLinks(resolved);
				})
				.catch(() => {
					// Fallback links already point at a real release.
				});
		};

		const section = sectionRef.current;
		if (!section || typeof IntersectionObserver === "undefined") {
			loadLatestRelease();
		} else {
			observer = new IntersectionObserver(
				(entries) => {
					if (!entries.some((entry) => entry.isIntersecting)) return;
					observer?.disconnect();
					loadLatestRelease();
				},
				{ rootMargin: "800px 0px" },
			);
			observer.observe(section);
		}

		return () => {
			cancelled = true;
			observer?.disconnect();
		};
	}, [sectionRef]);

	return links;
}

function PlatformButton({ icon, platform, format, href }: PlatformButtonProps) {
	return (
		<a
			href={href}
			target="_blank"
			rel="noopener noreferrer"
			className="group flex items-center gap-4 rounded-[1.4rem] border border-white/[0.08] bg-white/[0.04] px-6 py-4 transition-all hover:-translate-y-0.5 hover:border-brand-500/25 hover:bg-white/[0.06] active:translate-y-px"
		>
			<div className="flex h-11 w-11 items-center justify-center rounded-2xl border border-white/[0.08] bg-white/[0.05] text-white/70 shadow-[inset_0_1px_0_rgba(255,255,255,0.08)] transition-colors group-hover:text-brand-300">
				{icon}
			</div>
			<div className="min-w-0 flex-1">
				<div className="text-sm font-semibold text-white/90">{platform}</div>
				<div className="text-xs text-white/30">{format}</div>
			</div>
			<ArrowSquareOut
				size={18}
				className="shrink-0 text-white/25 transition-colors group-hover:text-brand-300"
			/>
		</a>
	);
}

export function Download({ description }: DownloadProps) {
	const sectionRef = useRef<HTMLElement>(null);
	const links = useLatestReleaseLinks(sectionRef);

	return (
		<section ref={sectionRef} id="download" className="py-24 md:py-32">
			<div className="mx-auto max-w-6xl px-6">
				<div className="grid gap-10 lg:grid-cols-[0.85fr_1.15fr] lg:items-start">
					<div>
						<p className="text-[11px] uppercase tracking-[0.22em] text-brand-300">Download</p>
						<h2 className="mt-4 max-w-md text-3xl font-extrabold tracking-tight text-white sm:text-4xl md:text-5xl">
							Get Clapio on every desktop your team uses
						</h2>
						<p className="mt-5 max-w-[34rem] text-base leading-8 text-white/50 md:text-lg">
							{description}
						</p>
						<p className="mt-4 text-xs text-white/30">
							Version {links.version} · installs update themselves automatically ·{" "}
							<a
								href={RELEASES_PAGE}
								target="_blank"
								rel="noopener noreferrer"
								className="underline decoration-white/20 underline-offset-2 transition-colors hover:text-white/60"
							>
								all releases
							</a>
						</p>
					</div>

					<div className="grid gap-4 sm:grid-cols-2">
						<PlatformButton
							icon={<AppleLogo size={20} weight="fill" />}
							platform="macOS (Apple Silicon)"
							format=".dmg installer"
							href={links.macArm}
						/>
						<PlatformButton
							icon={<AppleLogo size={20} weight="fill" />}
							platform="macOS (Intel)"
							format=".dmg installer"
							href={links.macIntel}
						/>
						<PlatformButton
							icon={<Desktop size={20} weight="regular" />}
							platform="Windows"
							format=".exe installer"
							href={links.windows}
						/>
						<PlatformButton
							icon={<LinuxLogo size={20} weight="fill" />}
							platform="Linux"
							format=".AppImage"
							href={links.linux}
						/>
					</div>
				</div>
			</div>
		</section>
	);
}
